import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Typography from '@mui/material/Typography';
import { Divider} from '@mui/material';
import InfoItemBox from './InfoItemBox';
import { InfoItemBoxProps } from '../dataObjects/contentTypes';


const content: InfoItemBoxProps[] = [
  {
    title: "How to Set Up Your Depository Bank Account",
    blocks: [
      {
        type: "subheading",
        content: "Download and complete one of these forms:"
      },
      {
        type: "accordion",
        title: "Form CPF D103: Appointment of Depository Bank",
        content:[
          {type: "paragraph",  content: 
            [ 
              { type: "text", content: "Candidates and committees use this form to designate an authorized financial institution as a depository for their campaign funds. If you intend to raise or spend money for a political purpose, even if it is spending your own money, you must designate a depository bank and open a campaign account." },
            ]},
          {type: "paragraph",  content: 
            [ 
              {type: "text", content: "Download the " },
              { type: "link", text: "CPF D103 Form", url: "https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d103.pdf" }
            ]},
            
        ]
      },

      {
        type: "accordion",
        title: "Form CPF D104: Statement of Candidate Not Raising or Expending Campaign Funds (not for PACs)",
        content:[
          {type: "paragraph",  content: 
            [ 
              { type: "text", content: "This form is for candidates only. Candidates who do not intend to accept contributions, make expenditures (including expenditures of their own funds), or incur liabilities for any campaign-related purpose may file this form instead of designating a depository bank and opening a campaign bank account." },
            ]},
          {type: "paragraph",  content: 
            [ 
              {type: "text", content: "Download the " },
              { type: "link", text: "CPF D104 Form", url: "https://ocpf2.blob.core.windows.net/pdf/forms/cpf_d104.pdf" }
            ]},
            
        ]
      },
      {
        type: "subheading",
        content: "Your bank may require the following information to open a campaign account:"
      },
      {
        type: "list",
        listType: "bullet",
        items: [ 
          {
            content: [
              { type: "text", content: "A copy of your completed statement of organization form (CPF 101)." }
            ]
          },
          {
            content: [
              { type: "text", content: "An Employer Identification Number (EIN). The IRS allows you to apply for an " },
              { type: "link", text: "EIN online", url: "https://www.irs.gov/businesses/small-businesses-self-employed/apply-for-an-employer-identification-number-ein-online" },
              { type: "text", content: ". (click " },
              { type: "link", text: "here", url: "/PublicSearch/ViewDocument?id=4017"},
              { type: "text", content: " for OCPF's instructions on how to obtain an EIN)" }
            ]
          },
          
        ]
      },
      {
        type: "paragraph",
        content: [
          { type: "text", isBold: true, content: "E-mail, fax, mail or hand-deliver the completed D103 or D104 form to OCPF." }
        ]
      },
      {
        type: "paragraph",
        content: [
          { type: "text", isBold: true, content: "Candidates and committees should also consult the " },
          { type: "link", isBold: true, text: "Massachusetts Department of Revenue", url: "https://www.mass.gov/orgs/massachusetts-department-of-revenue" },
          { type: "text", isBold: true, content: " for information on any state tax requirements that may be applicable." }
        ]
      },
     
    ]
  },
  {
    title: "Additional Help & Information",
    blocks: [
      {
        type: "accordion",
        title: "What is the Depository Reporting System?",
        content:[
          {type: "paragraph",  content: 
            [ {type: "text", content: "The depository reporting system was established by Section 19 of M.G.L. Chapter 55, the Massachusetts campaign finance law." }
            ]},
            {type: "paragraph",  content: 
              [ {type: "text", content: "Candidates and committees in this reporting system arrange for their financial institutions to file monthly reports with OCPF listing their total monthly deposits and providing detailed information about the committee's expenditures. In addition, these candidates and committees file reports of contribution information, and, if applicable, detailed monthly reports for any reimbursement, subvendor or credit card payments made by the committee." }
              ]},
              {type: "paragraph",  content: 
                [ {type: "text", content: "These candidates and committees are also responsible for filing year-end summary reports." }
                ]}
        ]
      },
      {
        type: "accordion",
        title: "Who files in the Depository Reporting System?",
        content: [
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "Statewide (Constitutional) Offices" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "Governor, Lieutenant Governor, Attorney General, State Secretary, State Treasurer or State Auditor." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "Governor's Council" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "One of the eight district seats of the Executive Council." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "House & Senate" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "A candidate for the General Court is seeking a seat in the State House or State Senate." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "County and District Offices" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "County Clerks of Court, Commissioners and Treasurers; Registers of Deeds and Probate; District Attorneys; Sheriffs and Suffolk County Clerks of the Superior and Supreme Judicial Courts." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "Mayoral" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "Mayoral candidates in all cities in the Commonwealth of Massachusetts." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "City Councilors in Cities with 65,000+ Populations" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "This includes the communities of Boston, Brockton, Cambridge, Fall River, Framingham, Haverhill, Lawrence, Lowell, Lynn, Malden, New Bedford, Newton, Quincy, Somerville, Springfield, Waltham, and Worcester." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "State Party Committees" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "The state committees of political parties organized under M.G.L. chapter 52, section 1." },
            ]},
            { type: "paragraph", content: [
              { type: "text", isBold: true, content: "State Political Action Committees (PACs)" },
            ]},
            { type: "paragraph", content: [
              { type: "text", content: "PACs and People's Committees. (Click on the tab above for more information about People's Committees.)" },
            ]}
          ]
      }
    ]
  }
];


const BankDepository = (): JSX.Element => {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });


  return (
    <div>
        
    <Typography
          component="h1"
          variant="h4"
          align={'left'}
          data-aos={'fade-up'}
          gutterBottom
          sx={{
            fontWeight: 700,
          }}
          paddingTop={4}>
          Set Up Your Depository Bank Account
        </Typography>

        <Divider />
     {content.map((content, index) => (
      <InfoItemBox key={index} {...content} />
    ))}
  </div>
   
  );
};

export default BankDepository;
